import type {RefObject} from 'react';
import {useCallback, useRef} from 'react';

import {useFitMeasurement} from './useFitMeasurement';

/**
 * Whether the collapsible region currently sits on the same line as the fixed
 * region. Both wrappers share an offsetParent (the row), so a strict offsetTop
 * comparison answers "did the collapsible wrap onto a new line?" independent of
 * any row padding/border.
 *
 * Returns `true` (fits) whenever geometry is unavailable — refs not yet
 * attached, or an environment with no layout (happy-dom in tests, SSR) where
 * every offset reads 0 (`0 <= 0`). Defaulting to "fits" keeps the full content
 * visible, the content-complete fallback.
 */
const doesExpandedFit = (
  row: HTMLElement | null,
  fixedEl: HTMLElement | null,
  collapsibleEl: HTMLElement | null
): boolean => {
  if (!row || !fixedEl || !collapsibleEl) {
    return true;
  }
  return collapsibleEl.offsetTop <= fixedEl.offsetTop;
};

export type CollapseToFitOptions = {
  /**
   * Bump whenever the intrinsic width of the expanded/collapsed content changes
   * for a reason unrelated to a container resize (e.g. a label updates). A row
   * resize is always re-measured automatically; this covers everything else.
   */
  remeasureKey?: string | number;
};

export type CollapseToFitResult = {
  /** Attach to the row element — the width constraint and ResizeObserver target. */
  rowRef: RefObject<HTMLDivElement | null>;
  /** Attach to the wrapper around the always-visible ("fixed") content. */
  fixedRef: RefObject<HTMLDivElement | null>;
  /** Attach to the wrapper around the swappable ("collapsible") content. */
  collapsibleRef: RefObject<HTMLDivElement | null>;
  /**
   * `true` once measurement shows the expanded content cannot share a line with
   * the fixed content. While measuring (and by default) this is `false` so the
   * expanded content is rendered and therefore measurable.
   */
  collapsed: boolean;
};

/**
 * Headless engine for a responsive "fixed + binary swap" row: it renders the
 * expanded content optimistically, measures (pre-paint) whether it wrapped below
 * the fixed content, and reports `collapsed` so the consumer can swap to a
 * compact representation. Backed by {@link useFitMeasurement}, so it re-measures
 * on a ResizeObserver and whenever `remeasureKey` changes — no churn otherwise.
 * Pair with `overflow-clip` on the row so the transient measuring pass — where
 * the expanded content may briefly wrap — never paints.
 *
 * @example
 * const {rowRef, fixedRef, collapsibleRef, collapsed} = useCollapseToFit();
 * return (
 *   <div ref={rowRef} className="relative flex flex-wrap items-center overflow-clip">
 *     <div ref={fixedRef} className="flex shrink-0 items-center">{fixed}</div>
 *     <div ref={collapsibleRef} className="flex shrink-0 items-center">
 *       {collapsed ? compact : full}
 *     </div>
 *   </div>
 * );
 */
export const useCollapseToFit = ({
  remeasureKey
}: CollapseToFitOptions = {}): CollapseToFitResult => {
  const fixedRef = useRef<HTMLDivElement>(null);
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const measure = useCallback(
    (row: HTMLDivElement | null) => doesExpandedFit(row, fixedRef.current, collapsibleRef.current),
    []
  );
  const {rowRef, value} = useFitMeasurement<boolean>(measure, remeasureKey);
  return {rowRef, fixedRef, collapsibleRef, collapsed: value === false};
};
